import { useCallback, useEffect, useRef, useState, FunctionComponent, ReactElement, MouseEvent } from 'react'
import { get, debounce } from 'libraries/lodash'
import cx from 'classnames'
import Divider from '@material-ui/core/Divider'
import { scrollTo } from 'front-end-core/helpers/document'
import Comments from 'components/comments'
import AccordionHeaderContainer from 'redux/containers/components/accordion/AccordionHeaderContainer'
import { ContentItemContainerProps } from 'redux/containers/components/accordion/ContentItemContainer'
import { dismissPopover } from 'services/OverlayService'

const ContentItem: FunctionComponent<ContentItemProps> = ({
  props,
  state,
  renderContent,
  children: Admin,
  isLogged,
  highlight,
  deviceType: { isMobile },
  setAccordionHighlight,
  encryptionKeysLoaded,
  isCommentsFiltered,
  pageId
}) => {
  const { title, id, addComment, allContentExpanded, cxNames } = props
  const [expanded, setExpanded] = useState(false)
  const [userHasClicked, setUserHasClicked] = useState(false)
  const dividerBackground = get(cxNames, 'contentHeader.headerBackground', '')
  const scrollToRef = useRef()

  useEffect(() => {
    setExpanded(allContentExpanded && isLogged)
  }, [allContentExpanded, isLogged])

  useEffect(() => {
    if (highlight && highlight === '#'.concat(id) && isLogged) {
      setExpanded(true)
      props.setAllContentExpanded(true)
    }
  }, [highlight, setExpanded, id, isLogged])

  useEffect(() => {
    // delay to allow scroll debounce & loadData to finish when user clicks content question link
    if (expanded && highlight && highlight === '#'.concat(id)) {
      const debounceTime = !encryptionKeysLoaded ? 200 : isCommentsFiltered ? 400 : false

      if (debounceTime) {
        const debounceThis = debounce(() => {
          scrollTo({ pageId, element: scrollToRef.current, animate: true, useWindowScrollTo: isMobile })
        }, debounceTime)
        debounceThis()
      }
    }
  }, [highlight, expanded, encryptionKeysLoaded, isCommentsFiltered, scrollToRef])

  useEffect(() => {
    if (userHasClicked && expanded) {
      scrollTo({ pageId, element: scrollToRef.current, animate: true, useWindowScrollTo: isMobile })
    }
    setUserHasClicked(false)
  }, [userHasClicked, expanded])

  useEffect(() => {
    setExpanded(state.expanded > 0 && isLogged)
  }, [state.expanded, isLogged, scrollToRef])

  const isHighlighted: boolean = state.enableHighlight && highlight && highlight === '#'.concat(id)
  const className: string = cx(
    `${props.className} depth-${props.depth} bg-white`,
    {
      'highlight-yellow': isHighlighted
    }
  )
  const commentsId: string = id + '/comments'

  const handleOnClick = useCallback((e: MouseEvent<HTMLDivElement>) => {
    dismissPopover(e.target)
    e.preventDefault()
    setExpanded(expanded => !expanded)
    setUserHasClicked(true)
    setAccordionHighlight(null)
  }, [setExpanded, setUserHasClicked, setAccordionHighlight])

  const canRenderContent = ():boolean => {
    // Solution to FC-1536: schedules not expandable
    if (!props.prefix) {
      return (!title || expanded)
    }

    if (!props.parentTitle && expanded) {
      return true
    }
    if (props.parentTitle && title && expanded) {
      return true
    }
    return (!title && expanded)
  }

  const contentVisible: boolean = canRenderContent()

  return (
    <li
      className={className}
      id={id}
      ref={scrollToRef}
    >
      <div
        className='outline-0'
        onClick={handleOnClick}
      >
        <AccordionHeaderContainer
          {...props}
          {...state}
          {...{ isHighlighted, commentsId, expanded, handleOnClick }}
          sticky={0}
        />
      </div>
      <div className={cx('ph4 mv0', dividerBackground)}>
        <Divider variant='fullWidth' classes={{ middle: 'mv0 mh8 bt bt--phantom-grey' }} />
      </div>
      {Admin}
      {contentVisible && renderContent()}
      {contentVisible && (
        <Comments
          id={commentsId}
          comments={state.comments}
          commentTarget={state.commentTarget}
          commentReference={state.commentReference}
          commentCategory={state.commentCategory}
          addComment={addComment}
        />
      )}
    </li>
  )
}

interface ContentItemProps extends ContentItemContainerProps {
  props: any; // FIX ME
  state: any; // FIX ME
  renderContent: () => JSX.Element;
  children: ReactElement
}

export default ContentItem
